/**
 * One-Stop Command Center
 *
 * Unified 6-quadrant telemetry view (Decision · Catalysts · Data Health ·
 * Financials · Forecasting · Stability), polled from /api/command-center/snapshot
 * at 1-2s. This is the FOUNDATION layer: health-colored panels that render each
 * quadrant's scalar fields + a raw-log toggle. Rich per-quadrant widgets/charts are
 * the next (gated) sub-task — each panel is a drop-in slot for them.
 */

import React, { useEffect } from 'react';
import {
  Activity, Newspaper, Database, DollarSign, TrendingUp, ServerCog, RefreshCw,
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { useCommandCenterStore } from '../stores/commandCenterStore';
import type { CommandCenterQuadrant } from '../lib/api';

const isErr = (q: CommandCenterQuadrant | undefined): boolean => !!q && typeof q.error === 'string';

/** Render the scalar (number/string/bool) top-level fields of a heterogeneous
 *  quadrant payload as labeled stats; nested objects/arrays are summarized. */
const ScalarPreview: React.FC<{ data: CommandCenterQuadrant }> = ({ data }) => {
  const entries = Object.entries(data || {}).filter(([k]) => k !== 'error' && k !== 'quadrant');
  const scalars = entries.filter(([, v]) => v == null || ['number', 'string', 'boolean'].includes(typeof v));
  const nested = entries.filter(([, v]) => v != null && typeof v === 'object');
  if (scalars.length === 0 && nested.length === 0) {
    return <div className="text-sm text-muted-foreground">No data yet.</div>;
  }
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        {scalars.slice(0, 8).map(([k, v]) => (
          <div key={k} className="rounded-md bg-muted/40 px-2 py-1">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{k}</div>
            <div className="text-sm font-medium truncate">{String(v)}</div>
          </div>
        ))}
      </div>
      {nested.map(([k, v]) => (
        <div key={k} className="text-xs text-muted-foreground">
          {k}: {Array.isArray(v) ? `${(v as unknown[]).length} item(s)` : `${Object.keys(v as object).length} field(s)`}
        </div>
      ))}
    </div>
  );
};

const Quadrant: React.FC<{
  title: string;
  icon: React.ReactNode;
  data: CommandCenterQuadrant | undefined;
}> = ({ title, icon, data }) => {
  const errored = isErr(data);
  return (
    <Card className="flex flex-col">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="flex items-center gap-2 text-base">{icon}{title}</CardTitle>
        <Badge variant={errored ? 'destructive' : 'secondary'}>{errored ? 'degraded' : 'live'}</Badge>
      </CardHeader>
      <CardContent className="flex-1">
        {errored ? (
          <div className="text-sm text-destructive">{String(data?.error)}</div>
        ) : (
          <ScalarPreview data={data || {}} />
        )}
        {data && (
          <details className="mt-3">
            <summary className="cursor-pointer text-xs text-muted-foreground">raw telemetry</summary>
            <pre className="mt-1 max-h-48 overflow-auto rounded bg-muted/40 p-2 text-[11px]">
              {JSON.stringify(data, null, 2)}
            </pre>
          </details>
        )}
      </CardContent>
    </Card>
  );
};

const CommandCenter: React.FC = () => {
  const { snapshot, loading, error, lastUpdated, startPolling, stopPolling } = useCommandCenterStore();

  useEffect(() => {
    startPolling();
    return () => stopPolling();
  }, [startPolling, stopPolling]);

  const q = snapshot?.quadrants;

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="flex items-center gap-2 text-xl font-semibold">
          <Activity className="h-5 w-5" /> Command Center
        </h1>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
          {lastUpdated ? `updated ${new Date(lastUpdated).toLocaleTimeString()}` : 'connecting…'}
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTitle>Telemetry error</AlertTitle>
          <AlertDescription>{error} — showing last good snapshot.</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        <Quadrant title="Decision Engine" icon={<Activity className="h-4 w-4" />} data={q?.decision} />
        <Quadrant title="News Catalysts" icon={<Newspaper className="h-4 w-4" />} data={q?.catalysts} />
        <Quadrant title="Data Health" icon={<Database className="h-4 w-4" />} data={q?.data_health} />
        <Quadrant title="Financials" icon={<DollarSign className="h-4 w-4" />} data={q?.financials} />
        <Quadrant title="Forecasting" icon={<TrendingUp className="h-4 w-4" />} data={q?.forecasting as CommandCenterQuadrant} />
        <Quadrant title="Stability" icon={<ServerCog className="h-4 w-4" />} data={q?.stability} />
      </div>
    </div>
  );
};

export default CommandCenter;
